import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronRight, Copy, Eye, EyeOff, Loader2, Timer } from 'lucide-react';
import { StatusPill } from '@/components/primitives';
import {
  armMyChargerFirstConnection, revealMyChargerPassword, type MyChargerConnection,
} from '@/lib/api';
import {
  firstConnectionWindowOpen, msUntilWindowCloses, needsCredentialsReason, offersRearmBesidePassword,
  revealSecondsLeft, scheduleRevealClear, type ConnectionView,
} from '@/lib/homeChargerConnection';
import styles from './ConnectionDetailsCard.module.css';

/**
 * How to connect a registered charger (register-first, 2026-10-02): the
 * server URL and charger ID with Copy, and the password masked behind
 * "Reveal for 60 seconds" + Copy, cleared again after 60 s (the Admin
 * programming-card pattern). Owner surfaces only: a household member's view
 * never renders it, and the reveal endpoint refuses anyone but the owner.
 *
 *   variant 'setup'    expanded, titled "Set up your charger": right after
 *                      registering, and on My Home / the charger page while
 *                      the charger has never connected
 *   variant 'details'  a collapsed "Connection details" row once it has,
 *                      for setting it up again after a reset or a new router
 *
 * In first_connection mode there is no password to type: the card says to
 * leave it blank and shows the setup window, with a button to open a fresh
 * 30-minute window while no key has been sent. When a window closes with
 * no key sent (a charger that will not connect with a blank password), the
 * card shows the password instead and keeps that button beside it.
 */
export function ConnectionDetailsCard({
  chargerId, connection, variant, onChanged,
}: {
  chargerId: string;
  connection: ConnectionView;
  variant: 'setup' | 'details';
  /** After a re-armed window, so the parent refetches. */
  onChanged?: (next: MyChargerConnection | null) => void;
}) {
  const { t } = useTranslation('myHome');
  const [open, setOpen] = useState(variant === 'setup');
  useEffect(() => { if (variant === 'setup') setOpen(true); }, [variant]);

  const settingUp = connection.settingUp;
  const body = (
    <div className={styles.body}>
      <p className={styles.intro}>{t('connection.intro')}</p>
      <CopyRow label={t('connection.urlLabel')} value={connection.url} />
      <CopyRow label={t('connection.idLabel')} value={connection.identity} />
      <p className={styles.note}>{t('connection.matchNote')}</p>
      {connection.state === 'provisioning' && (
        <p className={styles.noteStrong}>{t('connection.provisioning')}</p>
      )}
      {connection.state === 'needs_credentials' && (
        <p className={styles.noteStrong}>
          {needsCredentialsReason(connection) === 'window_expired'
            ? t('connection.windowExpired')
            : t('connection.needsCredentials')}
        </p>
      )}
      {connection.passwordMode === 'password' && (
        <PasswordRow chargerId={chargerId} />
      )}
      {offersRearmBesidePassword(connection) && (
        <RearmButton chargerId={chargerId} onChanged={onChanged} />
      )}
      {connection.passwordMode === 'first_connection' && (
        <FirstConnectionRow
          chargerId={chargerId}
          connection={connection}
          onChanged={onChanged}
        />
      )}
      {connection.passwordMode === 'none' && (
        <div className={styles.field}>
          <span className={styles.label}>{t('connection.passwordLabel')}</span>
          <p className={styles.note}>{t('connection.noPassword')}</p>
        </div>
      )}
    </div>
  );

  if (variant === 'setup') {
    return (
      <section className={styles.card} aria-label={t('connection.title')}>
        <div className={styles.header}>
          <h2 className={styles.title}>{t('connection.title')}</h2>
          {settingUp && (
            <StatusPill tone="info" variant="soft" size="sm">{t('connection.waitingPill')}</StatusPill>
          )}
        </div>
        {body}
      </section>
    );
  }

  return (
    <section className={styles.card}>
      <button
        type="button"
        className={styles.toggle}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{t('connection.detailsTitle')}</span>
        <ChevronRight size={16} className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} aria-hidden />
      </button>
      {open && body}
    </section>
  );
}

function CopyRow({ label, value }: { label: string; value: string }) {
  const { t } = useTranslation('myHome');
  const [copied, setCopied] = useState(false);
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (permissions / insecure context): the value is
      // still shown for manual copy, so fail quietly.
    }
  };
  return (
    <div className={styles.field}>
      <span className={styles.label}>{label}</span>
      <div className={styles.row}>
        <span className={styles.value}>{value}</span>
        <button type="button" className={styles.btn} onClick={copy} aria-label={t('copy.ariaCopy', { label })}>
          {copied ? <><Check size={14} /> {t('copy.copied')}</> : <><Copy size={14} /> {t('copy.copy')}</>}
        </button>
      </div>
    </div>
  );
}

/** The masked password. A reveal is one audited server read, shown for the
 *  seconds the server allows, then cleared from state (and so from the
 *  page) by scheduleRevealClear. Unmount cancels the clear and drops it. */
function PasswordRow({ chargerId }: { chargerId: string }) {
  const { t } = useTranslation('myHome');
  const [revealed, setRevealed] = useState<{ value: string; at: number; seconds: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const cancelRef = useRef<(() => void) | null>(null);

  useEffect(() => () => { cancelRef.current?.(); }, []);
  // A 1 s tick for the countdown, only while something is shown.
  useEffect(() => {
    if (!revealed) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [revealed]);

  const hide = (): void => {
    cancelRef.current?.();
    cancelRef.current = null;
    setRevealed(null);
    setCopied(false);
  };

  const reveal = async (): Promise<void> => {
    setBusy(true);
    setError(false);
    try {
      const res = await revealMyChargerPassword(chargerId);
      if (!res.password) {
        // The mode moved on server side (first connection armed); the
        // parent's next poll redraws the card.
        setError(true);
        return;
      }
      cancelRef.current?.();
      const at = Date.now();
      setNow(at);
      setRevealed({ value: res.password, at, seconds: res.display_seconds });
      cancelRef.current = scheduleRevealClear(res.display_seconds, () => {
        cancelRef.current = null;
        setRevealed(null);
        setCopied(false);
      });
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  const copy = async (): Promise<void> => {
    if (!revealed) return;
    try {
      await navigator.clipboard.writeText(revealed.value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Shown on screen for manual copy.
    }
  };

  const left = revealed ? revealSecondsLeft(revealed.at, revealed.seconds, now) : 0;
  return (
    <div className={styles.field}>
      <span className={styles.label}>{t('connection.passwordLabel')}</span>
      <div className={styles.row}>
        {revealed
          ? <span className={styles.value}>{revealed.value}</span>
          : <span className={`${styles.value} ${styles.masked}`} aria-hidden>{'••••••••••••'}</span>}
        {revealed && (
          <button type="button" className={styles.btn} onClick={copy} aria-label={t('copy.ariaCopy', { label: t('connection.passwordLabel') })}>
            {copied ? <><Check size={14} /> {t('copy.copied')}</> : <><Copy size={14} /> {t('copy.copy')}</>}
          </button>
        )}
      </div>
      <div className={styles.revealRow}>
        {revealed ? (
          <>
            <button type="button" className={styles.btn} onClick={hide}>
              <EyeOff size={14} /> {t('connection.hide')}
            </button>
            <span className={styles.countdown}>
              <Timer size={12} aria-hidden /> {t('connection.hidesIn', { seconds: left })}
            </span>
          </>
        ) : (
          <button type="button" className={styles.btn} onClick={reveal} disabled={busy}>
            {busy
              ? <><Loader2 size={14} className={styles.spin} /> {t('connection.revealing')}</>
              : <><Eye size={14} /> {t('connection.reveal')}</>}
          </button>
        )}
      </div>
      {error && <p className={styles.error} role="alert">{t('connection.revealError')}</p>}
    </div>
  );
}

function FirstConnectionRow({
  chargerId, connection, onChanged,
}: {
  chargerId: string;
  connection: ConnectionView;
  onChanged?: (next: MyChargerConnection | null) => void;
}) {
  const { t } = useTranslation('myHome');
  const fc = connection.firstConnection;
  // The window closes on the clock, not on the next read: one redraw when
  // it ends, so a screen left open stops saying "open until" and offers
  // the re-arm button.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const ms = msUntilWindowCloses(fc, Date.now());
    if (ms === null) return;
    const id = window.setTimeout(() => setNow(Date.now()), ms + 250);
    return () => window.clearTimeout(id);
  }, [fc]);
  const windowOpen = firstConnectionWindowOpen(fc, now);
  const ends = fc?.window_ends_at ? new Date(fc.window_ends_at) : null;
  const time = ends
    ? ends.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : '';

  return (
    <div className={styles.field}>
      <span className={styles.label}>{t('connection.passwordLabel')}</span>
      <p className={styles.noteStrong}>{t('connection.firstConnectionPassword')}</p>
      {connection.state !== 'provisioning' && fc && (
        <>
          <p className={styles.note}>
            {windowOpen && time ? t('connection.windowOpen', { time }) : t('connection.windowClosed')}
          </p>
          {fc.can_rearm && !windowOpen && (
            <RearmButton chargerId={chargerId} onChanged={onChanged} />
          )}
        </>
      )}
    </div>
  );
}

/** "Open setup for 30 minutes": a fresh first-connection window. Shown in
 *  the setup row once a window has closed, and beside the password after a
 *  window closed with no key sent. The parent refetches on success. */
function RearmButton({
  chargerId, onChanged,
}: {
  chargerId: string;
  onChanged?: (next: MyChargerConnection | null) => void;
}) {
  const { t } = useTranslation('myHome');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const rearm = async (): Promise<void> => {
    setBusy(true);
    setError(false);
    try {
      onChanged?.(await armMyChargerFirstConnection(chargerId));
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className={styles.revealRow}>
        <button type="button" className={styles.btn} onClick={rearm} disabled={busy}>
          {busy
            ? <><Loader2 size={14} className={styles.spin} /> {t('connection.rearming')}</>
            : <><Timer size={14} /> {t('connection.rearm')}</>}
        </button>
      </div>
      {error && <p className={styles.error} role="alert">{t('connection.rearmError')}</p>}
    </>
  );
}
